import React, { useState, useMemo, useEffect, useRef, useId, useCallback } from "react";
import styled from "styled-components";
import { RightOutlined, DownOutlined } from "@ant-design/icons";
import { AutoComplete, Checkbox, Col, Empty, InputNumber, Row, Select, Switch, Tag } from "antd";
import { Button, Form, message, Input } from "antd";
import { useDebouncedSearch, useGetEventById, useGetUser } from "lib/hooks";
import { getSession } from "lib/utils/utils";
import SubmitButton from "../../profile/Button";
import { ReactComponent as SelectIcon } from "images/modal/Frameselect.svg";
import { useEditEventById } from "lib/hooks";
import { EditEventPayload } from "lib/hooks";
import { EnvironmentFilled } from "@ant-design/icons";
import { ILocation, RegionOption } from "lib/types";
import { Link, useNavigate } from "react-router-dom";
import { useAddressAutofillCore, useSearchBoxCore } from "@mapbox/search-js-react";
import { useEventContext } from "ui/components/event/eventContext";
import { getRegionsByCountryCode, parseLegacyLocationString, serializeLocation } from "lib/utils/location-utils";
import { routeNames } from "lib/routes";
import Map, { MapProps, Marker, NavigationControl } from "react-map-gl/mapbox";
import { MAPBOX_PUBLIC_KEY } from "lib/constants";
import { RequiredMark, VisibilityWrapper } from "ui/components/base";
import { countryOptions } from "lib/data/countries";
import TextArea from "antd/es/input/TextArea";
import { ButtonContainer } from "../../createevent/eventtype.style";
import "mapbox-gl/dist/mapbox-gl.css";
import { MarkerStroked } from "@alpaca-travel/react-maki-icons";
import LucideIcon from "ui/components/Icons/LucideIcon";
import { evendyTheme } from "ui/theme";
import { useParams } from "react-router-dom";
import type { SearchBoxSuggestion } from "@mapbox/search-js-core";
import {
  CardDisabled,
  CollapseButton,
  CollapsibleContent,
  ComingSoonButton,
  Container,
  ContentContainer,
  EventTypeCard,
  EventTypeCardDisabled,
  EventTypeContainer,
  EventTypeDescription,
  EventTypeLabel,
  Left,
  Right,
  SmallerText,
  SmallText,
  StatusLeft,
  StatusRight,
  SubmitContainer,
  TicketStatus,
} from "./TypeLocation.style";

const MAPBOX_TOKEN = MAPBOX_PUBLIC_KEY;

interface MapboxSelectedLocation {
  label: string;
  value: SearchBoxSuggestion;
}

const TypeLocation: React.FC = () => {
  const { id: rawEventId } = useParams<{ id: string }>();
  const { mutate: editEvent } = useEditEventById();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<MapboxSelectedLocation[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<MapboxSelectedLocation | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<ILocation | null>(null);
  const [initialLocation, setInitialLocation] = useState<ILocation | null>(null);
  const [isManualAddress, setIsManualAddress] = useState<boolean>(true);
  const [isVisible, setIsVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [form] = Form.useForm();
  const sessionTokenId = useId();

  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const mapRef = useRef<any>(null);

  const navigate = useNavigate();
  const autofill = useSearchBoxCore({ accessToken: MAPBOX_TOKEN, country: "ng" });

  const eventId = rawEventId ? Number(rawEventId) : null;
  const { data: existingEventData } = useGetEventById({ id: Number(rawEventId) });
  const { data: user } = useGetUser();

  const getCountryValueByName = (name: string) => {
    const country = countryOptions.find(c => c.label.toLowerCase() === name.toLowerCase());
    return country?.value || "";
  };

  useEffect(() => {
    if (existingEventData?.location) {
      try {
        let locationString = existingEventData.location;

        // Clean the string by removing all escape characters and quotes
        locationString = locationString.replace(/\\/g, "").replace(/"/g, "");

        // Try to parse as JSON (if it's JSON-formatted)
        try {
          const locationData = JSON.parse(locationString);
          // Clean all string fields and convert country name to value
          const cleanLocation = {
            streetName: locationData.streetName?.replace(/\\/g, "") || "",
            city: locationData.city?.replace(/\\/g, "") || "",
            adminArea: locationData.adminArea?.replace(/\\/g, "") || "",
            country:
              typeof locationData.country === "string"
                ? getCountryValueByName(locationData.country)
                : locationData.country || "",
          };

          // Set the form's initial values with the cleaned data
          form.setFieldsValue({ location: cleanLocation });
          setInitialLocation(cleanLocation);
        } catch {
          // Fallback for plain string format (e.g., "street, city, country")
          const parts = locationString.split(", ");
          const cleanLocation = {
            streetName: parts[0]?.replace(/\\/g, "") || "",
            city: parts[1]?.replace(/\\/g, "") || "",
            adminArea: parts[2]?.replace(/\\/g, "") || "",
            country: parts[3] ? getCountryValueByName(parts[3]) : "",
          };

          form.setFieldsValue({ location: cleanLocation });
          setInitialLocation(cleanLocation);
        }
      } catch (error) {
        console.error("Error parsing initial location:", error);
      }
    }
  }, [existingEventData]);

  const search = useCallback(
    async (value: string) => {
      if (searchValue?.length > 4) {
        const suggestionResults = await autofill.suggest(value, { sessionToken: sessionTokenId });
        setSuggestions(() =>
          suggestionResults.suggestions.map(result => {
            return { label: result.place_formatted, value: result };
          }),
        );
      }
    },
    [searchValue],
  );

  const debouncedSeach = useDebouncedSearch(search, 3000);

  const organizerEmail = useMemo(() => {
    const session = getSession();
    return user?.email || session?.email;
  }, [user]);

  const regions = useMemo(() => {
    if (form.getFieldValue(["location", "country"])) {
      return getRegionsByCountryCode(form.getFieldValue(["location", "country"]));
    }
  }, [form.getFieldValue(["location", "country"])]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedSeach(value);
  };

  const handleCardClick = (type: string) => {
    setSelectedType(prevType => (prevType === type ? null : type));
  };

  const { data: event, error: eventError } = eventId ? useGetEventById({ id: eventId }) : { data: null, error: null };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const handleMapboxSuggestionSelect = (suggestion: MapboxSelectedLocation) => {
    setSelectedSuggestion(suggestion);
    setSuggestions([]);
  };

  const handleMapMove = () => {
    const center = mapRef.current?.getMap()?.getCenter();
    if (center) {
      form.setFieldValue(["location", "latitude"], center.lat);
      form.setFieldValue(["location", "longitude"], center.lng);
    }
  };

  const handleManualSubmission = (values?: ILocation) => {
    const locationData = values || form.getFieldsValue().location;

    // Get country name from country code
    const countryName = countryOptions.find(c => c.value === locationData.country)?.label || locationData.country;

    // Create the display string directly (no JSON encoding)
    const locationString = [
      locationData.streetName,
      locationData.city,
      locationData.adminArea, // state/province
      countryName,
    ]
      .filter(Boolean) // Remove empty values
      .join(", "); // Join with commas

    if (isManualAddress && locationString) {
      editEvent(
        {
          id: Number(eventId),
          data: {
            ...existingEventData,
            type: String(selectedType),
            organizerEmail: organizerEmail,
            location: locationString, // Save as simple string
          },
        },
        {
          onSuccess: () => {
            message.success("Event updated successfully");
            navigate(routeNames.eventDate); // Add navigation if needed
          },
          onError: () => message.error("Failed to update the event location."),
        },
      );
    }
  };

  const handleSubmit = async () => {
    if (!eventId) {
      message.error("Create an event first");
      return;
    }

    if (!selectedType) {
      message.error("Please select an event type.");
      return;
    }

    try {
      const values = await form.validateFields();
      if (isManualAddress) {
        handleManualSubmission(values.location); // Pass the location data directly
      }
    } catch (error) {
      console.error("Validation failed:", error);
      message.error("Please fill in all required fields.");
    }
  };

  return (
    <Container>
      <CollapseButton onClick={toggleVisibility}>
        Type & Location {isVisible ? <DownOutlined /> : <RightOutlined />}
      </CollapseButton>
      <CollapsibleContent isVisible={isVisible}>
        <ContentContainer>
          <SmallText>Select Event Type</SmallText>
          <EventTypeContainer>
            <EventTypeCard selected={selectedType === "physical"} onClick={() => handleCardClick("physical")}>
              <Left>
                <EventTypeLabel selected={selectedType === "physical"}>Physical event</EventTypeLabel>
                <EventTypeDescription selected={selectedType === "physical"}>
                  At a venue or physical location
                </EventTypeDescription>
              </Left>
              <Right visible={selectedType === "physical"}>
                <SelectIcon />
              </Right>
            </EventTypeCard>

            <EventTypeCardDisabled selected={selectedType === "online"} disabled>
              <CardDisabled>
                <EventTypeLabel>Online event</EventTypeLabel>
                <ComingSoonButton>Coming Soon</ComingSoonButton>
              </CardDisabled>
              <EventTypeDescription>Virtual live stream hosting</EventTypeDescription>
            </EventTypeCardDisabled>

            <EventTypeCardDisabled selected={selectedType === "hybrid"} disabled>
              <CardDisabled>
                <EventTypeLabel>Hybrid event</EventTypeLabel>
                <ComingSoonButton>Coming Soon</ComingSoonButton>
              </CardDisabled>
              <EventTypeDescription>Host physically & virtually</EventTypeDescription>
            </EventTypeCardDisabled>
          </EventTypeContainer>

          <VisibilityWrapper isVisible={isManualAddress && selectedType === "physical"}>
            <Form
              form={form}
              onFinish={handleSubmit} // This will trigger when the form is submitted
              layout="vertical"
              initialValues={{ location: initialLocation || {} }}
              style={{ width: "100%", margin: "0 auto" }}
              requiredMark={RequiredMark}
              disabled={!isManualAddress}
              size="large"
              variant="outlined"
            >
              <Form.Item
                name={["location", "streetName"]}
                label="Street Name"
                rules={[{ required: true, message: "Street name is required" }]}
              >
                <Input autoComplete="address-line1" name="address-line1" placeholder="eg. 10 Niamey St" />
              </Form.Item>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name={["location", "country"]}
                    label="Country"
                    rules={[{ required: true, message: "Country is required" }]}
                  >
                    <Select
                      showSearch
                      optionFilterProp="label"
                      placeholder="Select Country"
                      options={countryOptions}
                      onChange={val => {
                        form.setFieldValue(["location", "adminArea"], undefined);
                        form.setFieldValue(["location", "country"], val);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name={["location", "adminArea"]} label="State / Province">
                    <VisibilityWrapper
                      isVisible={isManualAddress}
                      fallback={<Input autoComplete="address-level1" readOnly />}
                    >
                      <Select
                        onSelect={value => {
                          form.setFieldValue(["location", "adminArea"], value);
                        }}
                        options={regions}
                        placeholder={
                          form.getFieldValue(["location", "country"])
                            ? "Select a State / Province"
                            : "Select a country first"
                        }
                      />
                    </VisibilityWrapper>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                name={["location", "city"]}
                label="City"
                rules={[{ required: true, message: "City is required" }]}
              >
                <Input autoComplete="address-level2" name="address-level2" />
              </Form.Item>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name={["location", "postalCode"]} label="Postal Code">
                    <Input autoComplete="postal-code" name="postal-code" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name={["location", "unit"]} label="Unit / Apt / Suite">
                    <Input autoComplete="address-line2" name="address-line2" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name={["location", "streetPredirection"]} label="Street Direction">
                    <Input placeholder=" (e.g. N, S, W, SW)" autoComplete="off" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name={["location", "streetSuffix"]} label="Street Type">
                    <Input placeholder="(e.g. St, Ave)" autoComplete="off" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name={["location", "landmark"]} label="Landmark">
                    <TextArea autoComplete="off" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name={["location", "neighborhood"]} label="Neighborhood">
                    <TextArea autoComplete="off" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item name={["location", "plusCode"]} label="Open Location Code (Plus Code)">
                    <Input autoComplete="off" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item name={["location", "latitude"]} label="Latitude">
                    <InputNumber style={{ width: "100%" }} min={-90} max={90} step={0.000001} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item name={["location", "longitude"]} label="Longitude">
                    <InputNumber style={{ width: "100%" }} min={-180} max={180} step={0.000001} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </VisibilityWrapper>
          <VisibilityWrapper isVisible={selectedType === "physical" && !isManualAddress}>
            <Map
              ref={mapRef}
              initialViewState={{
                longitude: initialLocation?.longitude ?? 3.3792,
                latitude: initialLocation?.latitude ?? 6.5244,
                zoom: 10,
              }}
              mapStyle="mapbox://styles/mapbox/streets-v9"
              mapboxAccessToken={MAPBOX_TOKEN}
              onMoveEnd={handleMapMove}
              style={{
                display: "block",
                position: "relative",
                width: 530,
                height: 300,
                borderRadius: 8,
                overflow: "hidden",
                maxWidth: 530,
                backgroundColor: "#ececec",
              }}
            >
              <NavigationControl position="top-left" />
              {initialLocation?.longitude && initialLocation?.latitude && (
                <Marker longitude={initialLocation.longitude} latitude={initialLocation.latitude} color="red" />
              )}
            </Map>
          </VisibilityWrapper>

          <SubmitContainer>
            <SubmitButton form={form}>Save changes</SubmitButton>
          </SubmitContainer>
        </ContentContainer>
      </CollapsibleContent>
    </Container>
  );
};

export default TypeLocation;
